import { useCallback, useEffect, useState } from "react";
import {
  DocumentSubType,
  DocumentType,
  IncludedDocuments,
  PropoundingParty,
  ReactionType,
} from "@/briefpoint-client";
import type { DocumentClient, DocumentResponse } from "@/lib/types";
import { addUploadDate } from "@/lib/utils";
import { useDocumentApi } from "./useApi";
import useMixpanel from "./useMixpanel";

type UseDocumentsType = [
  () => Promise<void>,
  (files: File[], caseId?: string, isPleadingUpload?: boolean) => Promise<void>,
  (document: DocumentClient) => Promise<void>,
];
export default function useDocuments(
  loadDocs = true,
  caseId?: string,
): UseDocumentsType {
  const [documents, setDocuments] = useState<DocumentClient[] | null>(null);
  const documentApi = useDocumentApi();
  const [mixpanelTrack] = useMixpanel();

  const loadDocuments = useCallback(async () => {
    const documents = !caseId
      ? ((await documentApi.documentGetList({
          includedDocuments: IncludedDocuments.NonArchived,
        })) as DocumentResponse[])
      : ((await documentApi.documentGetCaseDocuments({
          caseId,
          includedDocuments: IncludedDocuments.NonArchived,
        })) as DocumentResponse[]);
    setDocuments(documents.map((d) => addUploadDate(d)));
  }, [documentApi, caseId]);

  const updateDocument = useCallback(
    async (doc: DocumentClient) => {
      await documentApi.documentUpdateDocument({
        documentId: doc.id,
        document: doc,
      });
      if (documents) {
        setDocuments([...documents]);
      }
    },
    [documentApi, documents],
  );

  async function uploadDocuments(
    files: File[],
    caseId?: string,
    isPleadingUpload?: boolean,
  ) {
    try {
      const uploaded = (await documentApi.documentPost({
        files,
        caseId,
        ...(isPleadingUpload
          ? {
              docSubType: DocumentSubType.Complaint,
              docType: DocumentType.Pleading,
            }
          : {}),
      })) as DocumentResponse[];
      if (uploaded && documents) {
        for (const doc of uploaded) {
          mixpanelTrack("DocumentUpload", {
            DocumentName: doc.name,
            DocumentId: doc.id,
          });
        }
        setDocuments(documents.concat(uploaded.map((d) => addUploadDate(d))));
      }
    } catch {
      // TODO: handle error
    }
  }

  useEffect(() => {
    if (loadDocs) loadDocuments();
  }, [loadDocuments, loadDocs]);

  return [loadDocuments, uploadDocuments, updateDocument];
}

const isPowerOfTwo = (x: number): boolean => {
  return x !== 0 && (x & (x - 1)) === 0;
};

function getEnumFlags<O extends object, K extends O[keyof O] = O[keyof O]>(
  obj: O,
): K[] {
  const isFlag = (arg: string | number | K): arg is K => {
    const nArg = Number(arg);
    const isNumber = !Number.isNaN(nArg);
    return isNumber && isPowerOfTwo(nArg);
  };

  const enumFlags: K[] = [];

  for (const key of Object.keys(obj)) {
    const nKey = Number(key);
    if (isFlag(nKey)) {
      enumFlags.push(nKey);
    }
  }

  return enumFlags;
}

export function GetEnumValues(values: number) {
  const flags = getEnumFlags(PropoundingParty); //should be able to get this to be generic, but idk how to typescript

  const enums: string[] = [];

  for (const ef of flags) {
    // Bitwise comparison
    if ((ef & values) !== 0) {
      enums.push(PropoundingParty[ef]);
    }
  }

  return enums;
}

export function GetReactionTypeEnumValues(values: number) {
  const flags = getEnumFlags(ReactionType); //should be able to get this to be generic, but idk how to typescript

  const enums: string[] = [];

  for (const ef of flags) {
    // Bitwise comparison
    if ((ef & values) !== 0) {
      enums.push(ReactionType[ef]);
    }
  }

  return enums;
}
